import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { formatDate as formatDateIntl, formatNumber } from '../../i18n/formatters';
import { categoryLabel } from './categoryLabel';

function gradeLabel(min, max) {
  if (min == null && max == null) return null;
  const fmt = g => g === 0 ? i18n.t('printables:grade.kindergarten') : String(g);
  if (min === max) return i18n.t('printables:grade.single', { grade: fmt(min) });
  return i18n.t('printables:grade.range', { min: fmt(min), max: fmt(max) });
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  return formatDateIntl(dateStr, { month: 'short', year: 'numeric' }) || null;
}

export default function PrintableDetail({ posterId, fetchPoster, fetchSimilarPosters, onClose, onPosterClick, onPrint, onBuy, purchaseCost }) {
  const { t } = useTranslation('printables');
  const [poster, setPoster] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);
  const [standardsExpanded, setStandardsExpanded] = useState(false);

  useEffect(() => {
    if (!posterId) return;
    setLoading(true);
    setImgError(false);
    setStandardsExpanded(false);
    Promise.all([
      fetchPoster(posterId),
      fetchSimilarPosters(posterId).catch(() => ({ posters: [] })),
    ]).then(([posterData, similarData]) => {
      setPoster(posterData);
      setSimilar(similarData.posters || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [posterId, fetchPoster, fetchSimilarPosters]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  if (!posterId) return null;

  const grade = poster ? gradeLabel(poster.gradeMin, poster.gradeMax) : null;
  const published = poster ? formatDate(poster.publishedAt) : null;
  const visibleStandards = poster?.standards?.length > 0
    ? (standardsExpanded ? poster.standards : poster.standards.slice(0, 3))
    : [];
  const hasMoreStandards = poster?.standards?.length > 3;

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(27,37,80,0.31)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{
        background: '#FFFFFF', borderRadius: 20,
        border: '1px solid rgba(27,37,80,0.076)',
        maxWidth: 1400, width: '95vw', maxHeight: '92vh', overflowY: 'auto',
        position: 'relative',
      }}>
        {/* Close button */}
        <button onClick={onClose} style={{
          position: 'absolute', top: 16, right: 16, zIndex: 2,
          width: 32, height: 32, borderRadius: '50%', border: 'none',
          background: '#FFFFFF', color: '#1B2550',
          fontSize: '1.1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          ✕
        </button>

        {loading ? (
          <div style={{ padding: 80, textAlign: 'center', color: 'rgba(27,37,80,0.38)' }}>{t('detail.loading')}</div>
        ) : !poster ? (
          <div style={{ padding: 80, textAlign: 'center', color: 'rgba(27,37,80,0.38)' }}>{t('detail.notFound')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {/* Top section: image left, info right */}
            <div style={{ display: 'flex', flexDirection: 'row', minHeight: 0 }}>
              {/* Left panel — image */}
              <div style={{
                width: '55%', flexShrink: 0,
                background: '#EEF0F6', borderRadius: '20px 0 0 0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 32,
              }}>
                {poster.previewUrl && !imgError ? (
                  <img
                    src={poster.previewUrl}
                    alt={poster.title}
                    onError={() => setImgError(true)}
                    style={{ maxHeight: '75vh', maxWidth: '100%', objectFit: 'contain', borderRadius: 8 }}
                  />
                ) : (
                  <div style={{ height: 400, display: 'flex', alignItems: 'center', color: 'rgba(27,37,80,0.19)', fontSize: '0.9rem' }}>
                    {t('detail.noPreview')}
                  </div>
                )}
              </div>

              {/* Right panel — info */}
              <div style={{
                flex: 1, overflowY: 'auto', maxHeight: '75vh',
                padding: '32px 36px',
                display: 'flex', flexDirection: 'column',
              }}>
                {/* Title */}
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#1B2550', lineHeight: 1.25 }}>
                  {poster.title}
                </h2>

                {/* Breadcrumbs */}
                {poster.breadcrumbs && poster.breadcrumbs.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                    {poster.breadcrumbs.map((b, i) => (
                      <span key={b.id} style={{ fontSize: '0.73rem', color: 'rgba(27,37,80,0.332)' }}>
                        {i > 0 && <span style={{ margin: '0 4px' }}>›</span>}
                        {b.displayName || categoryLabel(t, b.name)}
                      </span>
                    ))}
                  </div>
                )}

                {/* Badges */}
                <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                  {grade && (
                    <span style={{ fontSize: '0.75rem', fontWeight: 500, color: 'rgba(26,176,242,0.9)', background: 'rgba(26,176,242,0.12)', padding: '5px 12px', borderRadius: 8 }}>
                      {grade}
                    </span>
                  )}
                  {poster.styleType && (
                    <span style={{ fontSize: '0.75rem', fontWeight: 500, color: 'rgba(127,75,246,0.9)', background: 'rgba(127,75,246,0.12)', padding: '5px 12px', borderRadius: 8 }}>
                      {poster.styleType.replace(/_/g, ' ')}
                    </span>
                  )}
                  {poster.category && (
                    <span style={{ fontSize: '0.75rem', fontWeight: 500, color: 'rgba(38,199,116,0.9)', background: 'rgba(38,199,116,0.12)', padding: '5px 12px', borderRadius: 8 }}>
                      {poster.category.displayName || categoryLabel(t, poster.category.name)}
                    </span>
                  )}
                </div>

                {/* Divider */}
                <div style={{ height: 1, background: '#FFFFFF', margin: '20px 0' }} />

                {/* Description */}
                {poster.description && (
                  <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.7, color: 'rgba(27,37,80,0.57)' }}>
                    {poster.description}
                  </p>
                )}

                {/* Curriculum Standards */}
                {poster.standards && poster.standards.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: 'rgba(27,37,80,0.285)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                      {t('detail.curriculumStandards')}
                    </div>
                    {visibleStandards.map(s => (
                      <div key={s.id} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'baseline' }}>
                        <span style={{ fontSize: '0.7rem', fontFamily: 'monospace', color: 'rgba(26,176,242,0.8)', background: 'rgba(26,176,242,0.08)', padding: '2px 6px', borderRadius: 4, flexShrink: 0 }}>
                          {s.identifier}
                        </span>
                        <span style={{ fontSize: '0.78rem', color: 'rgba(27,37,80,0.427)', lineHeight: 1.3 }}>
                          {s.description}
                        </span>
                      </div>
                    ))}
                    {hasMoreStandards && (
                      <button
                        onClick={() => setStandardsExpanded(!standardsExpanded)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: '0.73rem', fontWeight: 500, color: 'rgba(26,176,242,0.7)',
                          padding: '4px 0', marginTop: 2,
                        }}
                      >
                        {standardsExpanded ? t('detail.showFewer') : t('detail.showAllStandards', { count: poster.standards.length })}
                      </button>
                    )}
                  </div>
                )}

                {/* Exactly two actions (owner 2026-10-26): Save to My library and Print, owned or not. */}
                {(
                  <button
                    onClick={() => onBuy?.(poster)}
                    disabled={!onBuy}
                    style={{
                      marginTop: 24, padding: '12px 20px', borderRadius: 12, border: 'none',
                      background: '#7F4BF6',
                      cursor: onBuy ? 'pointer' : 'not-allowed',
                      opacity: onBuy ? 1 : 0.6,
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={e => { if (onBuy) e.currentTarget.style.background = '#5F2FD1'; }}
                    onMouseLeave={e => { if (onBuy) e.currentTarget.style.background = '#7F4BF6'; }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M3 6h18M16 10a4 4 0 01-8 0" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#FFFFFF' }}>
                      {purchaseCost === 0
                        ? t('detail.buyFree')
                        : purchaseCost != null
                          ? t('detail.buyForCredits', { count: purchaseCost })
                          : t('detail.buyNoPrice')}
                    </span>
                  </button>
                )}
                {(
                  <button
                    onClick={() => onPrint?.(poster)}
                    disabled={!onPrint}
                    data-testid="detail-print"
                    style={{
                      marginTop: 10, padding: '12px 20px', borderRadius: 12, border: 'none',
                      background: '#26C774', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                      transition: 'background 0.15s ease',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#189A57'}
                    onMouseLeave={e => e.currentTarget.style.background = '#26C774'}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path d="M6 9V2h12v7" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <rect x="6" y="14" width="12" height="8" rx="1" stroke="#FFFFFF" strokeWidth="1.5" />
                    </svg>
                    <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#FFFFFF' }}>
                      {t('detail.print')}
                    </span>
                  </button>
                )}


                {/* Similar Posters — in right panel */}
                {similar.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: '0.73rem', fontWeight: 600, color: 'rgba(27,37,80,0.285)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                      {t('detail.similarPosters')}
                    </div>
                    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
                      {similar.map(s => (
                        <div
                          key={s.id}
                          onClick={() => onPosterClick(s.id)}
                          style={{
                            flexShrink: 0, width: 100, cursor: 'pointer', borderRadius: 10, overflow: 'hidden',
                            border: '1px solid rgba(27,37,80,0.057)', background: '#FFFFFF',
                            transition: 'transform 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                          onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                        >
                          {s.thumbnailUrl ? (
                            <img src={s.thumbnailUrl} alt={s.title} style={{ width: '100%', aspectRatio: '2/3', objectFit: 'cover', display: 'block' }} loading="lazy" />
                          ) : (
                            <div style={{ width: '100%', aspectRatio: '2/3', background: '#EEF0F6' }} />
                          )}
                          <div style={{
                            padding: '4px 6px', fontSize: '0.65rem', color: 'rgba(27,37,80,0.475)',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {s.title}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Metadata footer */}
                {(poster.viewCount > 0 || published) && (
                  <div style={{ marginTop: 16, fontSize: '0.72rem', color: 'rgba(27,37,80,0.237)' }}>
                    {poster.viewCount > 0 && t('detail.views', { count: poster.viewCount, formatted: formatNumber(poster.viewCount) })}
                    {poster.viewCount > 0 && published && '  ·  '}
                    {published && t('detail.published', { date: published })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
